/**
 * TabstopService - Parses and manages tabstop data from HTML data attributes
 * 
 * This service handles the parsing and validation of tabstop definitions stored
 * in data-tabstops attributes on paragraph-level elements (p, h1-h6).
 * 
 * Schema:
 * - data-tabstops: JSON array of tabstop objects
 * - Each tabstop: { position: number (points), alignment: string, leader: string }
 * - alignment: "left" | "right" | "center" | "decimal"
 * - leader: "none" | "dots" | "dashes" | "underline"
 * 
 * Tab characters are represented as: <span class="lp-tab" contenteditable="false">&#8203;</span>
 * 
 * @see plans/lp-001-tabs/tabstop-contract.md
 */
angular.module('LeasePilot').factory('TabstopService', function() {

  // Valid values for tabstop properties
  const VALID_ALIGNMENTS = ['left', 'right', 'center', 'decimal'];
  const VALID_LEADERS = ['none', 'dots', 'dashes', 'underline'];
  
  // Default tabstop interval in points (0.5 inches = 36 points)
  const DEFAULT_TABSTOP_INTERVAL = 36;

  // Tolerance when matching "same" tabstop positions across paragraphs
  const POSITION_MATCH_TOLERANCE = 0.5;
  
  // Selector for paragraph-level elements that can have tabstops
  const PARAGRAPH_SELECTOR = 'p, h1, h2, h3, h4, h5, h6';
  
  // CSS class for tab character spans
  const TAB_CLASS = 'lp-tab';

  /**
   * Validates a single tabstop object
   * @param {Object} tabstop - The tabstop object to validate
   * @returns {Object|null} - Validated tabstop or null if invalid
   */
  function validateTabstop(tabstop) {
    if (!tabstop || typeof tabstop !== 'object') {
      return null;
    }

    // Position must be a non-negative number
    const position = parseFloat(tabstop.position);
    if (isNaN(position) || position < 0) {
      console.warn('TabstopService: Invalid tabstop position:', tabstop.position);
      return null;
    }

    // Alignment defaults to 'left' if invalid or missing
    let alignment = (tabstop.alignment || '').toLowerCase();
    if (!VALID_ALIGNMENTS.includes(alignment)) {
      alignment = 'left';
    }

    // Leader defaults to 'none' if invalid or missing
    let leader = (tabstop.leader || '').toLowerCase();
    if (!VALID_LEADERS.includes(leader)) {
      leader = 'none';
    }

    return {
      position: position,
      alignment: alignment,
      leader: leader
    };
  }

  /**
   * Decodes HTML entities in a string (e.g. &quot; -> ") without interpreting as HTML.
   * Safe against XSS: uses string replacement only, no innerHTML/DOM parsing.
   * Docx2HTML encodes data-tabstops for safe HTML attributes; when the value
   * is read back (e.g. from serialized HTML) it may still contain literal entities.
   * @param {string} str - String that may contain HTML entities
   * @returns {string} - Decoded string
   */
  function decodeHtmlEntities(str) {
    if (typeof str !== 'string' || str === '') {
      return str;
    }
    const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
    return str.replace(/&(?:#(\d+)|#x([0-9a-fA-F]+)|(amp|lt|gt|quot|apos));/g, function(match, dec, hex, name) {
      if (dec !== undefined) {
        return String.fromCodePoint(parseInt(dec, 10));
      }
      if (hex !== undefined) {
        return String.fromCodePoint(parseInt(hex, 16));
      }
      if (name && named[name] !== undefined) {
        return named[name];
      }
      return match;
    });
  }

  /**
   * Parses the data-tabstops JSON attribute from an element
   * @param {HTMLElement} element - The element to parse
   * @returns {Array} - Array of validated tabstop objects, sorted by position
   */
  function parseTabstopsFromElement(element) {
    if (!element || !element.getAttribute) {
      return [];
    }

    let attr = element.getAttribute('data-tabstops');
    if (!attr || attr.trim() === '') {
      return [];
    }

    // Decode HTML entities (e.g. &quot; -> ") so JSON.parse gets valid JSON
    attr = decodeHtmlEntities(attr);

    let parsed;
    try {
      parsed = JSON.parse(attr);
    } catch (e) {
      console.warn('TabstopService: Failed to parse data-tabstops JSON:', attr, e);
      return [];
    }

    if (!Array.isArray(parsed)) {
      console.warn('TabstopService: data-tabstops is not an array:', parsed);
      return [];
    }

    // Validate each tabstop and filter out invalid ones
    const validated = parsed
      .map(validateTabstop)
      .filter(ts => ts !== null);

    // Sort by position (Word requires left-to-right order)
    validated.sort((a, b) => a.position - b.position);

    return validated;
  }

  /**
   * Finds the paragraph element containing or being the given element
   * @param {HTMLElement|Node} element - The element or node to start from
   * @returns {HTMLElement|null} - The paragraph element or null
   */
  function findParagraphElement(element) {
    if (!element) {
      return null;
    }

    // If it's a text node, get its parent
    if (element.nodeType === Node.TEXT_NODE) {
      element = element.parentElement;
    }

    if (!element || !element.closest) {
      return null;
    }

    // Check if element itself is a paragraph
    if (element.matches && element.matches(PARAGRAPH_SELECTOR)) {
      return element;
    }

    // Find closest paragraph ancestor
    return element.closest(PARAGRAPH_SELECTOR);
  }

  /**
   * Gets tabstops for a given element (finds containing paragraph first)
   * @param {HTMLElement|Node} element - Any element or node within a paragraph
   * @returns {Array} - Array of tabstop objects for the containing paragraph
   */
  function getTabstopsForElement(element) {
    const paragraph = findParagraphElement(element);
    if (!paragraph) {
      return [];
    }
    return parseTabstopsFromElement(paragraph);
  }

  /**
   * Sets tabstops on a paragraph element
   * @param {HTMLElement} element - The paragraph element
   * @param {Array} tabstops - Array of tabstop objects
   * @returns {boolean} - True if successful
   */
  function setTabstopsOnElement(element, tabstops) {
    const paragraph = findParagraphElement(element);
    if (!paragraph) {
      console.warn('TabstopService: Cannot set tabstops - no paragraph element found');
      return false;
    }

    // Validate and sort tabstops
    const validated = (tabstops || [])
      .map(validateTabstop)
      .filter(ts => ts !== null);
    validated.sort((a, b) => a.position - b.position);

    if (validated.length === 0) {
      // Remove attribute if no tabstops
      paragraph.removeAttribute('data-tabstops');
    } else {
      paragraph.setAttribute('data-tabstops', JSON.stringify(validated));
    }

    return true;
  }

  /**
   * Gets all paragraphs with tabstops in a container
   * @param {HTMLElement} container - The container to search in
   * @returns {Array} - Array of {element, tabstops} objects
   */
  function getAllTabstopsInContainer(container) {
    if (!container) {
      container = document;
    }

    const results = [];
    const paragraphs = container.querySelectorAll(PARAGRAPH_SELECTOR + '[data-tabstops]');
    
    paragraphs.forEach(para => {
      const tabstops = parseTabstopsFromElement(para);
      if (tabstops.length > 0) {
        results.push({
          element: para,
          tabstops: tabstops
        });
      }
    });

    return results;
  }

  /**
   * Finds the next tabstop position for a given horizontal position
   * @param {Array} tabstops - Array of tabstop objects
   * @param {number} currentPosition - Current position in points
   * @returns {Object|null} - Next tabstop object or null if none found
   */
  function findNextTabstop(tabstops, currentPosition) {
    if (!Array.isArray(tabstops) || tabstops.length === 0) {
      return null;
    }

    // Find the first tabstop beyond current position
    for (const ts of tabstops) {
      if (ts.position > currentPosition) {
        return ts;
      }
    }

    return null;
  }

  /**
   * Calculates the next tab position (either from defined tabstops or default interval)
   * @param {Array} tabstops - Array of tabstop objects
   * @param {number} currentPosition - Current position in points
   * @returns {number} - The position to move to (in points)
   */
  function calculateNextTabPosition(tabstops, currentPosition) {
    const nextTabstop = findNextTabstop(tabstops, currentPosition);
    
    if (nextTabstop) {
      return nextTabstop.position;
    }

    // Use default interval: round up to next interval
    return Math.ceil((currentPosition + 1) / DEFAULT_TABSTOP_INTERVAL) * DEFAULT_TABSTOP_INTERVAL;
  }

  /**
   * Checks if an element is a tab character element
   * @param {HTMLElement} element - The element to check
   * @returns {boolean} - True if it's a tab element
   */
  function isTabElement(element) {
    return element && 
           element.classList && 
           element.classList.contains(TAB_CLASS);
  }

  /**
   * Creates a tab character element
   * @returns {HTMLElement} - A new tab span element
   */
  function createTabElement() {
    const span = document.createElement('span');
    span.className = TAB_CLASS;
    span.setAttribute('contenteditable', 'false');
    // Use zero-width marker so tab span has no intrinsic glyph width.
    span.textContent = '\u200B';
    return span;
  }

  /**
   * Finds all tab elements within a container
   * @param {HTMLElement} container - The container to search in
   * @returns {Array} - Array of tab span elements
   */
  function findTabElements(container) {
    if (!container) {
      container = document;
    }
    return Array.from(container.querySelectorAll('.' + TAB_CLASS));
  }

  /**
   * Gets tabstops for multiple selected paragraphs
   * Returns merged view (tabstops common to all) or individual arrays
   * @param {Array} paragraphs - Array of paragraph elements
   * @param {boolean} mergeCommon - If true, return only common tabstops
   * @returns {Array|Object} - Tabstops array or object with per-paragraph data
   */
  function getTabstopsForParagraphs(paragraphs, mergeCommon) {
    if (!Array.isArray(paragraphs) || paragraphs.length === 0) {
      return mergeCommon ? [] : {};
    }

    if (mergeCommon) {
      // Find tabstops that exist in ALL paragraphs (by position)
      const allTabstops = paragraphs.map(p => parseTabstopsFromElement(p));
      
      if (allTabstops.length === 0) {
        return [];
      }

      // Start with first paragraph's tabstops
      let common = allTabstops[0];
      
      for (let i = 1; i < allTabstops.length; i++) {
        const otherTabstops = allTabstops[i];
        common = common.filter(ts =>
          otherTabstops.some(other => Math.abs(other.position - ts.position) <= POSITION_MATCH_TOLERANCE)
        );
      }
      
      return common;
    } else {
      // Return object keyed by some identifier
      const result = {};
      paragraphs.forEach((p, index) => {
        const id = p.getAttribute('section-id') || p.getAttribute('pid') || index;
        result[id] = parseTabstopsFromElement(p);
      });
      return result;
    }
  }

  /**
   * Applies tabstops to multiple paragraphs
   * @param {Array} paragraphs - Array of paragraph elements
   * @param {Array} tabstops - Tabstops to apply to all paragraphs
   * @returns {boolean} - True if all successful
   */
  function setTabstopsOnParagraphs(paragraphs, tabstops) {
    if (!Array.isArray(paragraphs)) {
      return false;
    }

    let allSuccess = true;
    paragraphs.forEach(p => {
      if (!setTabstopsOnElement(p, tabstops)) {
        allSuccess = false;
      }
    });

    return allSuccess;
  }

  // Public API
  const TabstopService = {
    // Constants
    VALID_ALIGNMENTS: VALID_ALIGNMENTS,
    VALID_LEADERS: VALID_LEADERS,
    DEFAULT_TABSTOP_INTERVAL: DEFAULT_TABSTOP_INTERVAL,
    PARAGRAPH_SELECTOR: PARAGRAPH_SELECTOR,
    TAB_CLASS: TAB_CLASS,

    // Parsing functions
    parseTabstopsFromElement: parseTabstopsFromElement,
    getTabstopsForElement: getTabstopsForElement,
    getAllTabstopsInContainer: getAllTabstopsInContainer,
    getTabstopsForParagraphs: getTabstopsForParagraphs,

    // Mutation functions
    setTabstopsOnElement: setTabstopsOnElement,
    setTabstopsOnParagraphs: setTabstopsOnParagraphs,

    // Utility functions
    findParagraphElement: findParagraphElement,
    findNextTabstop: findNextTabstop,
    calculateNextTabPosition: calculateNextTabPosition,
    validateTabstop: validateTabstop,

    // Tab element functions
    isTabElement: isTabElement,
    createTabElement: createTabElement,
    findTabElements: findTabElements
  };

  return TabstopService;
});
